<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  item: { type: Object, required: true },
});

defineEmits(['remove']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const contact = computed(() => props.item.contact || {});
const displayName = computed(
  () => contact.value.name || t('KANBAN.CARD.NO_NAME')
);
const labels = computed(() => contact.value.labels || []);

// Track pointer position on press so a drag (movement beyond a small
// threshold) does not trigger the click-to-navigate handler.
let pointerDownPosition = null;
const DRAG_THRESHOLD = 5;

const onPointerDown = event => {
  pointerDownPosition = { x: event.clientX, y: event.clientY };
};

const isDrag = event => {
  if (!pointerDownPosition) return false;
  const dx = Math.abs(event.clientX - pointerDownPosition.x);
  const dy = Math.abs(event.clientY - pointerDownPosition.y);
  return dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD;
};

const navigateToContact = event => {
  // Ignore if the gesture was a drag rather than a genuine click.
  if (isDrag(event)) {
    pointerDownPosition = null;
    return;
  }
  pointerDownPosition = null;

  const { accountId } = route.params;
  const contactValue = contact.value;
  if (!contactValue.id) return;

  if (contactValue.last_conversation_id) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        conversation_id: contactValue.last_conversation_id,
      },
    });
    return;
  }

  // Fallback: open the contact page when there is no conversation.
  router.push({
    name: 'contacts_edit',
    params: { accountId, contactId: contactValue.id },
  });
};
</script>

<template>
  <div
    class="relative flex flex-col gap-2 p-3 transition-shadow bg-n-solid-1 border rounded-lg shadow-sm cursor-grab border-n-weak hover:shadow-md group"
    @pointerdown="onPointerDown"
    @click="navigateToContact"
  >
    <button
      type="button"
      class="absolute z-10 items-center justify-center hidden p-1 rounded top-2 ltr:right-2 rtl:left-2 text-n-slate-10 hover:text-n-ruby-9 hover:bg-n-alpha-1 group-hover:flex"
      :aria-label="t('KANBAN.CARD.REMOVE')"
      @click.stop="$emit('remove', item)"
    >
      <Icon icon="i-lucide-x" class="size-3.5" />
    </button>
    <div class="flex items-center gap-2">
      <Avatar
        :src="contact.thumbnail"
        :name="displayName"
        :size="32"
        hide-offline-status
      />
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ displayName }}
        </span>
        <span v-if="contact.email" class="text-xs truncate text-n-slate-11">
          {{ contact.email }}
        </span>
        <span
          v-else-if="contact.phone_number"
          class="text-xs truncate text-n-slate-11"
        >
          {{ contact.phone_number }}
        </span>
      </div>
    </div>
    <div v-if="labels.length" class="flex flex-wrap gap-1">
      <span
        v-for="label in labels"
        :key="label"
        class="px-1.5 py-0.5 text-xxs font-medium rounded bg-n-slate-3 text-n-slate-11"
      >
        {{ label }}
      </span>
    </div>
  </div>
</template>
